'use client';

import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useFetcherCSR } from '@/lib/useFetcherCSR';

// --- 1. Define API Types (Matches your JSON structure) ---
interface ApiCoupon {
  id: number;
  coupon_code: string;
  title: string | null;
  coupon_type: string; // e.g., 'free_shipping', 'percent', 'fixed'
  discount: number;
  coupon_event: string | null;
  is_used: boolean;
  expired_at: string;
  created_at: string;
}

// --- 2. Define UI Types (Used within the component) ---
interface Coupon {
  id: number;
  couponCode: string;
  discount: number;
  couponType: string;
  title: string;
  couponEvent: string;
  expiredAt: string;
  isUsed: boolean;
}

// --- Constants ---
const AppColors = {
  primary: '#e5456d',
  secondary: '#2d2b3a',
  darkGrey: '#4f4f4f',
  darkerGrey: '#333333',
  buttonDisabled: '#bdbdbd',
};

export default function MyCouponsPage() {
  const router = useRouter();

  // Fetch Data
  const {
    data: apiResponse,
    isLoading,
    error,
  } = useFetcherCSR(`/api/v2/coupon-rewards`);

  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [selectedCouponCode, setSelectedCouponCode] = useState<string | null>(
    null
  );

  // --- 3. Map API Data to UI State ---
  useEffect(() => {
    if (apiResponse && apiResponse.data) {
      const mappedData: Coupon[] = apiResponse.data.map((item: ApiCoupon) => ({
        id: item.id,
        couponCode: item.coupon_code,
        discount: item.discount,
        couponType: item.coupon_type,
        // Handle null values from API
        title: item.title || 'Special Reward',
        couponEvent: item.coupon_event || 'Exclusive Offer',
        expiredAt: item.expired_at,
        isUsed: item.is_used,
      }));
      setCoupons(mappedData);
    }
  }, [apiResponse]);

  // --- Action Handler ---
  const handleAction = (coupon: Coupon) => {
    if (coupon.isUsed) return;

    const isCopied = selectedCouponCode === coupon.couponCode;

    if (isCopied) {
      // Step 2: Already copied? Navigate to shop.
      router.push('/shop');
    } else {
      // Step 1: Not copied yet? Copy to clipboard & change state.
      navigator.clipboard.writeText(coupon.couponCode).then(() => {
        setSelectedCouponCode(coupon.couponCode); // This updates the button to "Use Now"
        toast.success(`Coupon code copied!`, {
          position: 'bottom-right',
          autoClose: 1500,
          hideProgressBar: true,
        });
      });
    }
  };

  // Helper to render discount text based on type
  const renderDiscountText = (coupon: Coupon) => {
    if (coupon.couponType === 'free_shipping') {
      return 'Free Ship';
    }
    if (coupon.discount === 0) {
      return 'Free';
    }
    return `${coupon.discount} ${coupon.couponType === 'percent' ? '%' : 'TK'} OFF`;
  };

  return (
    <>
      <ToastContainer />

      <div className="dashboard__content">
        <div className="dashboard__content-header">
          <h5>My Coupons</h5>
        </div>

        <div className="dashboard__content-info">
          <div className="account-pill">
            {isLoading ? (
              // --- Loading State ---
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : coupons.length === 0 ? (
              // --- Empty State ---
              <div className="text-center py-5">
                <p className="text-muted">
                  No coupons available at the moment.
                </p>
              </div>
            ) : (
              // --- Coupon Loop ---
              coupons.map((coupon) => {
                const isCopied = selectedCouponCode === coupon.couponCode;

                return (
                  <div
                    key={coupon.id}
                    className="bg-white rounded mb-4 position-relative overflow-hidden shadow-sm"
                    style={{ minHeight: '170px' }}
                  >
                    <div className="d-flex justify-content-between align-items-center p-4 h-100">
                      {/* Left Side: Discount */}
                      <div
                        style={{ width: '140px' }}
                        className="d-flex flex-column align-items-center text-center"
                      >
                        <h3
                          className="m-0 fw-bold"
                          style={{ color: AppColors.primary }}
                        >
                          {renderDiscountText(coupon)}
                        </h3>
                        <span className="text-secondary fw-medium mt-1 text-truncate w-100">
                          {coupon.title}
                        </span>
                      </div>

                      {/* Divider */}
                      <div
                        style={{
                          height: '100px',
                          borderLeft: '2px dashed #e0e0e0',
                        }}
                      ></div>

                      {/* Right Side: Info & Action */}
                      <div
                        style={{ width: '160px' }}
                        className="d-flex flex-column justify-content-between h-100"
                      >
                        <div className="mb-2">
                          <h6
                            className="fw-bold m-0 text-truncate"
                            style={{ color: AppColors.secondary }}
                          >
                            {coupon.couponEvent}
                          </h6>
                        </div>

                        <div className="d-flex flex-column gap-2">
                          <small
                            className="text-muted"
                            style={{ fontSize: '0.8rem' }}
                          >
                            Expires: {coupon.expiredAt}
                          </small>
                          <button
                            type="button"
                            onClick={() => handleAction(coupon)}
                            disabled={coupon.isUsed}
                            className={`trk-btn trk-btn--sm px-3 py-1 fw-medium ${
                              coupon.isUsed
                                ? 'trk-btn--secondary' // Used state
                                : isCopied
                                  ? 'trk-btn trk-btn--primary' // Use Now state
                                  : 'trk-btn trk-btn--outline' // Copy Now state
                            }`}
                            style={{
                              backgroundColor: coupon.isUsed
                                ? AppColors.buttonDisabled
                                : isCopied
                                  ? AppColors.primary // Blue/Pink when ready to use
                                  : AppColors.secondary, // Dark when ready to copy
                              color: '#fff',
                              border: 'none',
                              cursor: coupon.isUsed ? 'not-allowed' : 'pointer',
                              borderRadius: '4px',
                              transition: 'all 0.3s ease',
                            }}
                          >
                            {coupon.isUsed
                              ? ' Used '
                              : isCopied
                                ? 'Use Now'
                                : 'Copy Now'}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Used Overlay */}
                    {coupon.isUsed && (
                      <div
                        className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
                        style={{
                          zIndex: 10,
                          backgroundColor: 'rgba(255, 255, 255, 0.6)',
                        }}
                      >
                        <div
                          style={{
                            transform: 'rotate(-30deg)',
                            border: `3px solid ${AppColors.darkGrey}`,
                            padding: '5px 20px',
                            borderRadius: '8px',
                            backgroundColor: 'rgba(255,255,255,0.8)',
                          }}
                        >
                          <h2 className="m-0 fw-bold">USED</h2>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </>
  );
}
